// ─────────────────────────────────────────────────────────────
// SIGNATURE TACTILE — canvas doigt/souris/stylet
// Usage : <SignaturePad label="Signature du bailleur" onChange={dataUrl => ...} />
// onChange reçoit un dataURL PNG, ou null si effacé
// ─────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";

export default function SignaturePad({ label, onChange, height = 130 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Adapter la résolution au pixel ratio pour une signature nette
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, height);
    ctx.strokeStyle = "#1a3a5c";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [height]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk.current) { hasInk.current = true; setEmpty(false); }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInk.current && onChange) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, height);
    hasInk.current = false;
    setEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <label style={{ fontSize: 12, color: "#4a6a8a", fontWeight: 600 }}>{label}</label>
        {!empty && (
          <button
            style={{ background: "none", border: "none", color: "#a32d2d", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            onClick={clear}
          >✕ Effacer</button>
        )}
      </div>
      <div style={{ position: "relative" }}>
        <canvas
          ref={canvasRef}
          style={{
            width: "100%", height, borderRadius: 10, display: "block",
            border: empty ? "1.5px dashed #b0c4d8" : "1.5px solid #2e86c1",
            background: "white", touchAction: "none", cursor: "crosshair",
          }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {empty && (
          <span style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", color: "#b0c4d8", fontSize: 13, pointerEvents: "none" }}>
            ✍️ Signez ici avec le doigt ou la souris
          </span>
        )}
      </div>
    </div>
  );
}
